/**
 * Smart phase guidance — never auto-switches phases 2→6. Returns the id of the
 * NEXT phase the closer is likely about to enter, so the Phase Tracker can
 * pulse that pill. Closer stays in control.
 *
 * V0.3.4 guardrail: later transitions require CLOSER language, not just
 * prospect or item-counting heuristics. Stops the tracker from over-eagerly
 * suggesting Prescription / Pitch / Close / Post-Sell.
 */
const DISCOVERY_KEYS = [
  "pain_points",
  "goals",
  "previous_attempts",
  "cost_of_inaction",
  "decision_maker",
  "financial_situation",
];

// Prospect-side signals — still useful as supporting evidence.
const PRICING_RE = /\b(price|pricing|cost|invest|investment|payment|charge|how much|fee|tier|package|monthly|per month|per year|annual)\b/;
const BUYING_RE = /\b(when can we|sign me up|let'?s do (it|this)|let'?s go|i'?m in|ready to (start|go)|how do (i|we) (get )?start|how do we move forward|i want this|count me in|i'?m ready)\b/;
const CONFIRM_RE = /\b(let'?s do it|sounds (great|good)|i'?ll do it|i want to do this|count me in|all right (let'?s|let me)|i'?m sold|deal|let'?s go ahead)\b/;

// Closer-side phase intent. Phase pulse only fires when the CLOSER has
// actually started speaking the language of the next phase. Stops Discovery
// from prematurely pulsing Prescription on the prospect's pricing question.
const CLOSER_PRESCRIPTION_RE = /\b(let me (share|tell you) what (i think|i'?d (suggest|recommend))|here'?s what (i'?d|i would|i) (suggest|recommend|propose|do)|based on (what|everything) you'?ve (told me|said)|the way i'?d approach this|what i'?m hearing is|so what i'?m gathering)\b/;
const CLOSER_PITCH_RE = /\b(let me walk you through|here is the (program|package|offer|solution)|what we offer|our (program|package|solution)|here'?s how (it|this) works|here'?s what it (looks like|costs)|investment for this)\b/;
const CLOSER_CLOSE_RE = /\b(are you ready to (move forward|get started)|when would you (want|like) to start|does (this|that) sound good|are you (in|ready)|can we get you started|how would you like to (proceed|move forward)|should we (do|get) this|are you ready to make a decision)\b/;
const CLOSER_POSTSELL_RE = /\b(welcome (to|aboard)|let'?s (set up|book) your (kickoff|onboarding)|i'?ll send you|next steps are|congratulations on (the|your))\b/;

export function getSuggestedNextPhase({ phase, state, transcript }) {
  if (!phase) return null;
  const list = transcript || [];
  const recent = list.slice(-10);
  const recentAll = recent.map((l) => l.text.toLowerCase()).join("  ");
  const recentCloser = recent
    .filter((l) => l.speaker === "closer")
    .map((l) => l.text.toLowerCase())
    .join("  ");

  switch (phase) {
    case "upfront":
      // Upfront → Discovery is the ONLY auto-advance candidate. Pulse early so
      // the dashboard always pulls them into Discovery quickly.
      if (list.length >= 4) return "discovery";
      return null;

    case "discovery": {
      // V0.3.6: when ALL 6 discovery items are captured, pulse Prescription
      // even without explicit closer-prescription language — the call is
      // clearly past Discovery at that point. Otherwise still require BOTH
      // sufficient discovery (>=4 items) AND closer-prescription language so
      // we don't over-eagerly pulse mid-discovery.
      const filled = DISCOVERY_KEYS.filter((k) => (state[k] || []).length > 0).length;
      if (filled >= DISCOVERY_KEYS.length) {
        return "prescription";
      }
      if (filled >= 4 && CLOSER_PRESCRIPTION_RE.test(recentCloser)) {
        return "prescription";
      }
      return null;
    }

    case "prescription":
      // Closer must actually be pitching (presenting program / pricing).
      if (CLOSER_PITCH_RE.test(recentCloser) && PRICING_RE.test(recentAll)) {
        return "pitch";
      }
      return null;

    case "pitch":
      // Closer is asking for the close OR prospect is sending buying signals.
      if (CLOSER_CLOSE_RE.test(recentCloser) || BUYING_RE.test(recentAll)) {
        return "close";
      }
      return null;

    case "close":
      // Closer is onboarding OR prospect has explicitly confirmed.
      if (CLOSER_POSTSELL_RE.test(recentCloser) || CONFIRM_RE.test(recentAll)) {
        return "postsell";
      }
      return null;

    default:
      return null;
  }
}

/** First-empty discovery item — used in Compact Mode as "ask about X next". */
export function firstMissingDiscoveryItem(state) {
  const labels = {
    pain_points: "Pain Points",
    goals: "Goals",
    previous_attempts: "Previous Attempts",
    cost_of_inaction: "Cost of Inaction",
    decision_maker: "Decision Maker",
    financial_situation: "Financial Situation",
  };
  for (const k of DISCOVERY_KEYS) {
    if ((state[k] || []).length === 0) return labels[k];
  }
  return null;
}

/** Static, phase-specific "next action" headline for Compact Mode. */
export function suggestedNextAction({ phase, state }) {
  switch (phase) {
    case "upfront":
      return "Confirm time, agenda, and earn the right to a yes-or-no decision.";
    case "discovery": {
      const missing = firstMissingDiscoveryItem(state);
      return missing
        ? `Ask about ${missing}.`
        : "Recap their words back to them. Confirm you've heard everything.";
    }
    case "prescription":
      return "Diagnose first. Recap current state → desired state → bridge.";
    case "pitch":
      return "Sell the outcome, not the features. Price first, terms second.";
    case "close":
      return "Handle objections without breaking trust. Use their own words.";
    case "postsell":
      return "Lock in the win. Set the kickoff calendar invite right now.";
    default:
      return "Stay present. SCH is taking notes.";
  }
}
